import {
  ENROLLMENT_MAX_USES,
  branchRecord,
  clientRecord,
  createEnrollmentTokenResponse,
  createdUser,
  enrollmentTokenSummary,
  passwordResetResponse,
  permission as permissionSchema,
  role as roleSchema,
  scopeType as scopeTypeSchema,
  userRecord,
  type BranchRecord,
  type ClientRecord,
  type CreatedUser,
  type EnrollmentTokenFormat,
  type EnrollmentTokenSummary,
  type NodeType,
  type PasswordResetResponse,
  type Permission,
  type Role,
  type ScopeType,
  type UserRecord,
  type UserStatusFilter,
} from '@occ/contracts';
import { del, get, patch, post, put } from '../api/http';
import { ROUTES } from '../api/routes';
import { parseNodeRow, type NodeRow } from './nodes';

/**
 * Administración del tenant (Módulo 8, Fase 4 del PRD).
 *
 * Cuatro bloques con distinto grado de realidad en el backend, y conviene no
 * confundirlos:
 *
 * - **jerarquía** (clientes, sucursales, alias y traslado de nodos),
 *   **emisión de tokens de enrollment** y **usuarios y permisos** (Módulo 9,
 *   `apps/backend/src/users/`): implementados y verificados;
 * - **bitácora y branding**: el modelo de datos existe (`packages/db` tiene
 *   `audit_logs`) pero **no hay controlador que los exponga**. Las funciones de
 *   aquí llaman a la ruta que les corresponderá y quien las use pinta el motivo
 *   del 404.
 *
 * Los parsers son propios y no esquemas de zod porque el panel no depende de zod
 * —solo lo arrastra `@occ/contracts`— y porque una forma que el backend todavía
 * no ha fijado se lee mejor con tolerancia que con validación estricta. Es el
 * mismo criterio que `parseNodeRow`.
 */

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const asArray = (payload: unknown): unknown[] => (Array.isArray(payload) ? payload : []);

const asDate = (value: unknown): Date | null => {
  if (typeof value !== 'string' && !(value instanceof Date)) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// ── Clientes ────────────────────────────────────────────────────────────────

export const listClients = async (): Promise<ClientRecord[]> =>
  asArray(await get<unknown>(ROUTES.clients)).map((item) => clientRecord.parse(item));

export const createClient = (name: string, notes: string): Promise<ClientRecord> =>
  post(ROUTES.clients, { name, notes: notes || undefined }, clientRecord);

export const updateClient = (
  id: string,
  changes: { name?: string; notes?: string },
): Promise<ClientRecord> => patch(ROUTES.client(id), changes, clientRecord);

export const deleteClient = (id: string): Promise<void> => del(ROUTES.client(id));

// ── Sucursales ──────────────────────────────────────────────────────────────

export const listBranches = async (clientId?: string): Promise<BranchRecord[]> =>
  asArray(await get<unknown>(ROUTES.branches, undefined, clientId ? { clientId } : undefined)).map(
    (item) => branchRecord.parse(item),
  );

export const createBranch = (
  clientId: string,
  name: string,
  address: string,
): Promise<BranchRecord> =>
  post(ROUTES.branches, { clientId, name, address: address || undefined }, branchRecord);

export const updateBranch = (
  id: string,
  changes: { name?: string; address?: string },
): Promise<BranchRecord> => patch(ROUTES.branch(id), changes, branchRecord);

export const deleteBranch = (id: string): Promise<void> => del(ROUTES.branch(id));

// ── Nodos ───────────────────────────────────────────────────────────────────

/**
 * Renombrar y trasladar. Un nodo no se crea desde el panel —aparece al
 * enrolarse— ni se borra: lo que se hace con una máquina que sale del parque es
 * desinstalar el agente, y borrar la fila dejaría su bitácora sin sujeto.
 */
export const updateNode = async (
  id: string,
  changes: {
    alias?: string;
    branchId?: string;
    provider?: string | null;
    monthlyCostCents?: number | null;
    nodeType?: NodeType | null;
  },
): Promise<NodeRow> => parseNodeRow(await patch<unknown>(ROUTES.node(id), changes));

// ── Tokens de enrollment (M2 §3) ────────────────────────────────────────────

/**
 * De qué clase es el secreto que se pide (plan de enrolamiento por código, §5).
 *
 * - `long`: el `occ1_…` de 41 caracteres. Lo correcto para GPO e Intune.
 * - `short`: seis caracteres de base32 de Crockford, para teclearlos en la caja.
 * - `manual`: el que fija el administrador, `FRUTAL-7K2M`.
 *
 * Se reexporta de `@occ/contracts` y no se redeclara: allí es donde lo lee
 * también el catálogo de acciones, y dos listas de tres literales son dos listas
 * que se pueden separar.
 */
export type { EnrollmentTokenFormat, EnrollmentTokenSummary };

export type EnrollmentTokenRequest = {
  clientId: string;
  branchId: string;
  suggestedAlias?: string;
  expiresInMinutes: number;
  maxUses: number;
  format?: EnrollmentTokenFormat;
  /** Solo con `format: 'manual'`: el texto del código. */
  code?: string;
  /** Solo con `format: 'manual'`: declara que el código lleva sufijo aleatorio. */
  randomSuffix?: boolean;
};

export type IssuedEnrollmentToken = {
  token: string;
  format: EnrollmentTokenFormat;
  expiresAt: Date;
  installCommandLinux: string;
  installCommandWindows: string;
  /**
   * El de Windows 7 (F2.5 del plan de compatibilidad). Puede no venir: lleva
   * hashes pineados que el backend solo puede resolver si el canal publica una
   * versión con artefacto `windows7`, el instalador está en disco y hay base HTTP
   * de bootstrap configurada. Cuando falta, el motivo viene en el campo de abajo.
   */
  installCommandWindows7?: string | null;
  installCommandWindows7Unavailable?: string | null;
  maxUses: number;
};

/**
 * Los topes que valida el backend, copiados de
 * `apps/backend/src/enrollment/enrollment.contracts.ts`.
 *
 * Es la misma duplicación que ya existía con `MULTI_USE_MAX_EXPIRY_MINUTES` y por
 * el mismo motivo: esos contratos todavía no han subido a `@occ/contracts`
 * —siguen en el backend, esperando la integración de la Ola 1— y el formulario
 * necesita esconder lo que el backend rechazaría en vez de mandarlo para recibir
 * un 400. Cuando suban, esto se importa y estas líneas se van.
 */
export const MULTI_USE_MAX_EXPIRY_MINUTES = 24 * 60;
/**
 * Este sí subió ya: lo necesita el catálogo de acciones para publicar la emisión
 * por MCP, así que se importa en vez de repetirlo. Se reexporta con el nombre que
 * usa el formulario para no tocar la pantalla por un renombrado.
 */
export const MAX_USES_CEILING = ENROLLMENT_MAX_USES;
/** Aleatorio: 30 min por defecto, 8 h de tope (§10). */
export const SHORT_CODE_DEFAULT_EXPIRY_MINUTES = 30;
export const SHORT_CODE_MAX_EXPIRY_MINUTES = 8 * 60;
/** Manual **pelado**, sin sufijo: 15 min por defecto y 2 h de tope (§5.1). */
export const BARE_MANUAL_DEFAULT_EXPIRY_MINUTES = 15;
export const BARE_MANUAL_MAX_EXPIRY_MINUTES = 2 * 60;

export const issueEnrollmentToken = async (
  request: EnrollmentTokenRequest,
): Promise<IssuedEnrollmentToken> => {
  const payload = await post<unknown>(ROUTES.enrollmentTokens, request);
  const base = createEnrollmentTokenResponse.parse(payload);
  const record = asRecord(payload);
  const declared = record.maxUses;
  return {
    ...base,
    // `maxUses` y `format` no están en `createEnrollmentTokenResponse` de
    // `@occ/contracts` sino en la extensión del backend
    // (`enrollment.contracts.ts`), que todavía no ha subido al paquete
    // compartido. Se leen con tolerancia y con los valores que tenían antes de
    // que existieran: un uso y un token largo.
    maxUses: typeof declared === 'number' && declared > 0 ? declared : 1,
    format: asFormat(record.format),
  };
};

const asFormat = (value: unknown): EnrollmentTokenFormat =>
  value === 'short' || value === 'manual' ? value : 'long';

/** Respuesta de `POST /enrollment/code/check` (§6). */
export type EnrollmentCodeCheck = {
  available: boolean;
  /** Motivo cuando no vale, ya redactado por el backend. */
  reason: string | null;
  /** Cómo queda el código al normalizarlo, que es lo que se emitiría. */
  normalized: string;
};

/**
 * ¿Está libre este código manual?
 *
 * El motivo lo redacta el backend, que es quien tiene delante la lista de
 * rechazo y los códigos vivos; el formulario solo lo pinta. Duplicar los textos
 * aquí sería tener dos versiones de la misma regla y enterarse cuando se
 * separen.
 */
export const checkEnrollmentCode = async (request: {
  clientId: string;
  branchId: string;
  code: string;
}): Promise<EnrollmentCodeCheck> => {
  const record = asRecord(await post<unknown>(ROUTES.enrollmentCodeCheck, request));
  return {
    available: record.available === true,
    reason: typeof record.reason === 'string' ? record.reason : null,
    normalized: typeof record.normalized === 'string' ? record.normalized : request.code,
  };
};

/**
 * Los códigos y tokens vivos del tenant (`GET /enrollment/tokens`, §11).
 *
 * Con esquema de `@occ/contracts` y no con un parser tolerante a mano, al
 * contrario que el resto de este archivo: esta forma **sí** la fijó el backend y
 * la comparte con el catálogo de acciones, así que validarla es gratis y una
 * discrepancia se ve aquí en vez de en una celda vacía de la tabla.
 *
 * No trae el secreto ni su hash. No es un olvido: el secreto no existe en la base
 * —solo su sha256— y el hash no sale por decisión explícita del backend.
 */
export const listEnrollmentTokens = async (): Promise<EnrollmentTokenSummary[]> =>
  asArray(asRecord(await get<unknown>(ROUTES.enrollmentTokens)).tokens).map((item) =>
    enrollmentTokenSummary.parse(item),
  );

/**
 * Revoca uno. Deja de servir en el mismo instante.
 *
 * Se descarta la respuesta: el backend devuelve el resumen de lo que cayó —le
 * hace falta a quien revoca por MCP, que no tiene la tabla delante— y la pantalla
 * ya tiene esa fila; lo que necesita después es volver a listar, para que lo que
 * se ve sea lo que hay.
 */
export const revokeEnrollmentToken = (id: string): Promise<void> => del(ROUTES.enrollmentToken(id));

/** Estado de la CA interna. Sí existe: `GET /enrollment/ca`. */
export type CertificateAuthorityStatus = {
  commonName: string;
  serialNumber: string;
  notAfter: Date | null;
  expiresInDays: number | null;
  renewalDue: boolean;
  retainedAuthorities: number;
};

export const fetchCertificateAuthority = async (): Promise<CertificateAuthorityStatus> => {
  const record = asRecord(await get<unknown>(ROUTES.enrollmentCa));
  return {
    commonName: String(record.commonName ?? '—'),
    serialNumber: String(record.serialNumber ?? '—'),
    notAfter: asDate(record.notAfter),
    expiresInDays: typeof record.expiresInDays === 'number' ? record.expiresInDays : null,
    renewalDue: record.renewalDue === true,
    retainedAuthorities:
      typeof record.retainedAuthorities === 'number' ? record.retainedAuthorities : 0,
  };
};

// ── Usuarios y permisos (Módulo 9) ───────────────────────────────────────────

/**
 * Administración de operadores y de su alcance.
 *
 * Ya existe en el backend (`apps/backend/src/users/`), así que aquí se valida
 * con los esquemas de `@occ/contracts` en vez de con parsers tolerantes: la
 * forma está fijada y una respuesta que no cumpla el contrato es un fallo que
 * conviene ver, no un campo que rellenar por omisión. Es lo contrario del
 * criterio de `parseNodeRow`, y la diferencia es justo que aquí ya hay contrato.
 */
export type { UserRecord } from '@occ/contracts';

export type UserFilters = {
  role?: Role;
  scopeType?: ScopeType;
  /** Con `scopeType`: quién alcanza ese cliente, sucursal o nodo. */
  scopeId?: string;
  status?: UserStatusFilter;
  search?: string;
};

export const listUsers = async (filters: UserFilters = {}): Promise<UserRecord[]> =>
  asArray(
    await get<unknown>(ROUTES.users, undefined, {
      role: filters.role,
      scopeType: filters.scopeType,
      // El alcance solo se manda acompañado de su tipo: suelto no identifica
      // nada, porque el mismo uuid puede ser de un cliente o de un nodo.
      scopeId: filters.scopeType ? filters.scopeId : undefined,
      status: filters.status,
      search: filters.search,
    }),
  ).map((item) => userRecord.parse(item));

export type NewUser = {
  email: string;
  name: string;
  /**
   * Vacío = el OCC genera una temporal y la devuelve una sola vez. Es la forma
   * de invitar sin correo saliente: quien da el alta la entrega por el canal que
   * ya use con esa persona.
   */
  password?: string;
  permissions?: Permission[];
};

export const createUser = (input: NewUser): Promise<CreatedUser> =>
  post(
    ROUTES.users,
    {
      email: input.email.trim(),
      name: input.name.trim(),
      password: input.password ? input.password : undefined,
      permissions: input.permissions?.length ? input.permissions : undefined,
    },
    createdUser,
  );

/**
 * `role` reescribe el rol de **todas** las concesiones que ya tiene, sin tocar
 * los alcances: es lo que significa "ascender a este técnico". Cambiar dónde
 * entra es la otra operación (`grantPermission`).
 */
export const updateUser = (
  id: string,
  changes: { name?: string; role?: Role; isActive?: boolean },
): Promise<UserRecord> => patch(ROUTES.user(id), changes, userRecord);

export const deleteUser = (id: string): Promise<void> => del(ROUTES.user(id));

export const listUserPermissions = async (id: string): Promise<Permission[]> =>
  asArray(await get<unknown>(ROUTES.userPermissions(id))).map((item) =>
    permissionSchema.parse(item),
  );

/**
 * `PUT` y no `POST`: la clave es `(usuario, alcance)` y volver a conceder sobre
 * el mismo ámbito sustituye el rol, así que el panel no necesita saber si el
 * permiso ya existía.
 */
export const grantPermission = (
  userId: string,
  scopeType: ScopeType,
  scopeId: string,
  role: Role,
): Promise<UserRecord> =>
  put(
    ROUTES.userPermission(userId, scopeTypeSchema.parse(scopeType), scopeId),
    { role: roleSchema.parse(role) },
    userRecord,
  );

export const revokePermission = (
  userId: string,
  scopeType: ScopeType,
  scopeId: string,
): Promise<void> => del(ROUTES.userPermission(userId, scopeType, scopeId));

/**
 * Devuelve una contraseña temporal de un solo uso; el usuario tiene que
 * cambiarla al entrar. La ruta se compone a mano porque `ROUTES` todavía no
 * tiene entrada para ella —la cablea quien integra— y duplicar el prefijo aquí
 * sería la segunda copia de la misma cadena.
 */
export const resetUserPassword = (id: string): Promise<PasswordResetResponse> =>
  post(`${ROUTES.user(id)}/password-reset`, undefined, passwordResetResponse);

// ── Branding del tenant (RF-21, pendiente en el backend) ─────────────────────

/**
 * Branding del tenant.
 *
 * Los colores son cadenas CSS libres y no un formato cerrado a propósito:
 * `src/index.css` declara las variables en `oklch`, pero un administrador va a
 * pegar el hexadecimal de su manual de marca. Validar el formato aquí obligaría a
 * convertirlo; dejarlo pasar lo resuelve el navegador, y un valor inválido se ve
 * al instante en la vista previa.
 */
export type Branding = {
  /** Nombre que sustituye a «Omnia Control Center» en la barra lateral. */
  productName: string;
  /** URL o `data:` del logo. Vacío = se usa el nombre en texto. */
  logoUrl: string;
  brandColor: string;
  /** Vacío = se mantiene el valor del tema por omisión. */
  surfaceLight: string;
  surfaceDark: string;
  textLight: string;
  textDark: string;
};

export const EMPTY_BRANDING: Branding = {
  productName: '',
  logoUrl: '',
  brandColor: '',
  surfaceLight: '',
  surfaceDark: '',
  textLight: '',
  textDark: '',
};

const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

export const parseBranding = (value: unknown): Branding => {
  const record = asRecord(value);
  const colors = asRecord(record.colors);
  return {
    productName: asText(record.productName),
    logoUrl: asText(record.logoUrl),
    // Se acepta plano y anidado bajo `colors`: cuando el backend fije la forma,
    // una sola de las dos ramas sobrará y la otra ya funcionará.
    brandColor: asText(colors.brand) || asText(record.brandColor),
    surfaceLight: asText(colors.surfaceLight) || asText(record.surfaceLight),
    surfaceDark: asText(colors.surfaceDark) || asText(record.surfaceDark),
    textLight: asText(colors.textLight) || asText(record.textLight),
    textDark: asText(colors.textDark) || asText(record.textDark),
  };
};

export const fetchBranding = async (): Promise<Branding> =>
  parseBranding(await get<unknown>(ROUTES.branding));

export const saveBranding = async (branding: Branding): Promise<Branding> =>
  parseBranding(
    await put<unknown>(ROUTES.branding, {
      productName: branding.productName,
      logoUrl: branding.logoUrl,
      colors: {
        brand: branding.brandColor,
        surfaceLight: branding.surfaceLight,
        surfaceDark: branding.surfaceDark,
        textLight: branding.textLight,
        textDark: branding.textDark,
      },
    }),
  );
